// peek.ts — pure Helfer für das Termin-Peek-Popover (Einzelklick, A16) und das
// Rechtsklick-Menü mit Quick-Reschedule (A14, Entscheidung 63) des Kalender-
// Neubaus (`/termine`, Termine-SP4).
//
// Bewusst OHNE React/DOM, damit die Logik unter vitest (node-Env, nur *.test.ts)
// testbar bleibt: Zeitraum-Label, Art-Badge-Beschriftung, Verschiebe-Rechnung
// (Dauer bleibt konstant) und die Viewport-Klemmung der Popover-Position.
// Die Komponenten leben in TerminPeekPopover.tsx / TerminContextMenu.tsx.

import moment from "moment";
import { ensureMomentDe } from "../../utils/momentDe";
import { allDayBereichLabel, istMehrtagsAllDay } from "./allDay";
import type { CalendarTerminClass } from "./types";

// Datums-Labels laufen über moment — Locale defensiv sicherstellen (SP1).
ensureMomentDe();

/** Anzeige-Label je Termin-Klasse (Art-Badge im Peek, A13: Anzeige bleibt). */
export const ART_LABEL: Record<CalendarTerminClass, string> = {
    kundentermin: "Kundentermin",
    austausch: "Austausch / Intern",
    sonstige: "Sonstige",
    block: "Block",
    abwesenheit: "Abwesenheit",
};

/**
 * Zeitraum-Zeile des Peek-Popovers:
 * - zeitgebunden, gleicher Tag:  „Mo. 06.07.2026, 09:00 – 10:30"
 * - zeitgebunden, über Nacht:    „Mo. 06.07.2026, 22:00 – Di. 07.07.2026, 01:00"
 * - ganztägig, ein Tag:          „Ganztägig, Mo. 06.07.2026"
 * - ganztägig, mehrtägig:        „Ganztägig, 06.07. – 10.07." (Graph-Ende exklusiv)
 */
export function zeitraumLabel(start: Date, end: Date, allDay: boolean): string {
    if (allDay) {
        if (istMehrtagsAllDay(start, end)) {
            return `Ganztägig, ${allDayBereichLabel(start, end)}`;
        }
        return `Ganztägig, ${moment(start).format("ddd DD.MM.YYYY")}`;
    }
    const s = moment(start).format("ddd DD.MM.YYYY, HH:mm");
    if (moment(start).isSame(end, "day")) {
        return `${s} – ${moment(end).format("HH:mm")}`;
    }
    return `${s} – ${moment(end).format("ddd DD.MM.YYYY, HH:mm")}`;
}

/**
 * Quick-Reschedule (Entscheidung 63): neues Datum + neue Startzeit, die DAUER
 * bleibt konstant (Outlook-„Verschieben"). Bei Ganztagsterminen (`neueStartZeit
 * = null`) wandert nur der Datumsteil — eine mehrtägige Spanne behält ihre
 * Länge. Ungültige Zeit-Strings fallen auf die Original-Uhrzeit zurück.
 */
export function verschobenerZeitraum(
    origStart: Date,
    origEnd: Date,
    neuesDatum: Date,
    neueStartZeit: string | null,
): { start: Date; end: Date } {
    const dauerMs = origEnd.getTime() - origStart.getTime();
    const start = new Date(neuesDatum);
    if (neueStartZeit === null) {
        // Ganztägig: lokale Mitternacht (Graph-Ganztags-Konvention, allDay.ts).
        start.setHours(0, 0, 0, 0);
    } else {
        const m = /^(\d{1,2}):(\d{2})$/.exec(neueStartZeit.trim());
        const h = m ? Number(m[1]) : origStart.getHours();
        const min = m ? Number(m[2]) : origStart.getMinutes();
        start.setHours(h, min, 0, 0);
    }
    return { start, end: new Date(start.getTime() + dauerMs) };
}

/**
 * Start/Ende als NAIVE-LOKALE ISO-Strings — wie der Submit-/Reschedule-Pfad in
 * OutlookTermine.tsx:506-507; NIE toISOString() (sonst Doppel-Offset gegen
 * timeZone:'Europe/Berlin'). Seit SP4 hier single-sourced (CalendarView und
 * Quick-Reschedule teilen die Funktion).
 */
const pad = (n: number) => String(n).padStart(2, "0");
export const naiveIso = (d: Date): string =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes(),
    )}:${pad(d.getSeconds())}`;

/** Ankerpunkt des Peeks/Kontextmenüs (Viewport-Koordinaten, position:fixed). */
export interface PeekAnchor {
    x: number;
    y: number;
}

/**
 * Popover-Position am Anker in den Viewport klemmen: bevorzugt RECHTS neben dem
 * Anker; reicht der Platz nicht, klappt es nach links. Vertikal wird nur
 * geklemmt (nie über den Rand). Reine Funktion → testbar.
 */
export function clampPopoverPosition(
    anchor: PeekAnchor,
    size: { width: number; height: number },
    viewport: { width: number; height: number },
    abstand = 8,
): { top: number; left: number } {
    let left = anchor.x + abstand;
    if (left + size.width > viewport.width - abstand) {
        left = anchor.x - size.width - abstand;
    }
    left = Math.max(abstand, Math.min(left, viewport.width - size.width - abstand));
    let top = anchor.y;
    top = Math.max(abstand, Math.min(top, viewport.height - size.height - abstand));
    return { top, left };
}
